import { Card, Flex, Spin } from 'antd';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import {
  useGetAllProducts,
  useGetCustomerSession,
  useHasSubscription
} from 'lib/hooks/tanstack/use-getClinicInfo';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useErrorList, UseErrorListReturn } from 'lib/utils/useErrorList';
import React from 'react';
import { SubscriptionPackages } from './_components/subscription-packages';
import { Redirect } from 'react-router-dom';
import { ClinicRoutes } from 'routes';

declare global {
  // eslint-disable-next-line
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'pricing-table-id'?: string;
          'publishable-key'?: string;
          'customer-session-client-secret'?: string;
        },
        HTMLElement
      >;
    }
  }
}

export interface BillingSubscriptionViewProps {
  isLoading: boolean;
  clientSecret?: string;
  errorList: UseErrorListReturn;
  initialTimeLeft?: number;
  onSessionExpired?: () => void;
  productsData?: {
    prices: {
      id: string;
      unit_amount: number | null;
      lookup_key: string | null;
      /** @enum {string} */
      type: 'one_time' | 'recurring';
      product: {
        id: string;
        name: string;
        marketing_features: {
          name: string;
        }[];
      };
    }[];
    currency: string;
  };
}

export function BillingSubscriptionView({
  isLoading,
  errorList,
  productsData
}: BillingSubscriptionViewProps) {
  if (isLoading) {
    return (
      <PrintableRootPage title="Subscription setup">
        <Flex align="center" justify="center" style={{ minHeight: '200px' }}>
          <Spin size="large" />
        </Flex>
      </PrintableRootPage>
    );
  }

  const hasPackages = (productsData?.prices || []).some(_ => _.type === 'recurring');

  return (
    <PrintableRootPage title="Subscription setup">
      <ErrorListDisplay errorList={errorList} />
      <Flex gap={16} vertical>
        <Flex vertical gap={16 / 2}>
          <Card>
            <Flex gap={16} vertical>
              {hasPackages ? (
                <>
                  <Text>
                    To continue with billing setup, please select one of the
                    following subscriptions.
                  </Text>
                  <SubscriptionPackages productsData={productsData} />
                </>
              ) : (
                <Text>
                  No packages are available for your organisation at this time. Please contact sales@concussionrx.com to find a product that works for you.
                </Text>
              )}
            </Flex>
          </Card>
        </Flex>
      </Flex>
    </PrintableRootPage>
  );
}

export function BillingSubscriptionPage() {
  const { currentClinic } = useClinicianContext();
  const customerSessionQuery = useGetCustomerSession(currentClinic);
  const hasSubscriptionQuery = useHasSubscription(currentClinic);
  const allProductsQuery = useGetAllProducts(currentClinic);
  const errorList = useErrorList();

  useQueryErrorNotifier(customerSessionQuery, errorList, 'CustomerSession');
  useQueryErrorNotifier(hasSubscriptionQuery, errorList, 'HasSubscription');
  useQueryErrorNotifier(allProductsQuery, errorList, 'AllProducts');

  const isLoading =
    customerSessionQuery.isLoading ||
    hasSubscriptionQuery.isLoading ||
    allProductsQuery.isLoading;

  const hasSubscription =
    hasSubscriptionQuery.data?.data.hasPaymentMethod === true;

  if (hasSubscription) {
    return <Redirect to={ClinicRoutes.ClinicBillingSettings} />;
  }

  const rawData = customerSessionQuery.data?.data;
  const clientSecret =
    typeof rawData === 'string' ? rawData : rawData?.hasPaymentMethod;

  return (
    <BillingSubscriptionView
      isLoading={isLoading}
      clientSecret={clientSecret}
      errorList={errorList}
      productsData={allProductsQuery.data?.data}
    />
  );
}
